import { type FC, useMemo, useRef, useState } from "react";
import { match } from "ts-pattern";

import type { DocumentMetadata } from "@moment/api-collab/api-types";
import {
	useDocumentFromCurrentRoute,
	usePageFromCurrentRoute,
	useWorkspaceFromCurrentRoute,
} from "@moment/state";
import { LoadingSkeleton, cn } from "@moment/ui";

import { Editor } from "~/components/canvas/editor/BigRichTextEditor";
import { EditorHeader } from "~/components/canvas/editor/EditorHeader";
import { usePrintArea } from "~/components/common/print-area/usePrintArea";
import { usePageData } from "~/state";
import { useSelectAllCellsReady } from "~/state/machines/cells";

import { Row } from "../../layout/row/Row";
import { StaticDocumentHead } from "../StaticDocumentHead";
import { CANVAS_BODY_ID } from "../constants";
import { Byline } from "./Byline";
import { Footer } from "./Footer";
import { Header } from "./Header";

export type CanvasProps = {
	canvas: DocumentMetadata;
	pageId?: string;
	pageMetadata?: DocumentMetadata["pagesMetadata"][number];
};

/**
 * <CanvasView/>
 * This component is essentially the canvas content
 *
 * TODO:
 * Refactor this component so that it uses "runtime" and "canvas" from "useEasel"
 * instead of receiving them as props
 */
export const PublishedDocument: FC<CanvasProps> = ({
	canvas,
	pageId: pageIdProp,
	pageMetadata: pageMetadataProp,
}) => {
	// Hooks
	const allCellsReady = useSelectAllCellsReady();
	const [isFirstPageLoad, setIsFirstPageLoad] = useState(true);
	const { printAreaContentRef } = usePrintArea();

	const { workspaceName } = useWorkspaceFromCurrentRoute();
	const { documentId } = useDocumentFromCurrentRoute();
	const { pageMetadata: routePageMetadata } = usePageFromCurrentRoute();

	// Prefer props (SSR/published) and fall back to route-derived metadata.
	const pageMetadata = pageMetadataProp ?? routePageMetadata ?? null;
	const pageId = pageIdProp ?? pageMetadata?.id ?? routePageMetadata?.id ?? "";

	const pageData = usePageData(documentId, pageId ?? "");

	const showSkeleton = useMemo(() => {
		if (isFirstPageLoad && allCellsReady) {
			/* if we are still on first page load and all cells are initialized then turn off "firstPageLoad" and return false */
			setIsFirstPageLoad(false);
		} else if (isFirstPageLoad && !allCellsReady) {
			/* if we are on first page load and cells are still loading */
			return true;
		}

		return false;
	}, [allCellsReady, isFirstPageLoad]);

	// Refs
	const editorContainerRef = useRef<HTMLDivElement>(null);

	const canvasWritable = false;

	return (
		<>
			{/* ------ Client Meta Tags ------ */}
			<StaticDocumentHead canvas={canvas} pageData={pageData} pageSlug={pageMetadata?.slug} />

			{/* ------ TOP BAR ------ */}
			<div className="@container/main relative rich-text bg-primary border-b">
				<Row>
					<Header workspaceName={workspaceName} documentMetadata={canvas} />
				</Row>
			</div>
			<main className="bg-primary @container/main relative flex flex-1 flex-col focus:outline-none">
				{/* --- CANVAS --- */}
				<div className="relative flex flex-auto" ref={printAreaContentRef}>
					<div
						className={cn(
							"absolute",
							"print:static",
							"flex",
							"print:block",
							"inset-0",
							//overflow-y-hidden fixes a weird bug where if the window gets smaller you get multiple vertical scrollbars
							"overflow-y-hidden overflow-x-hidden",
							"print:overflow-visible",
							"print:bg-primary",
							"overscroll-auto"
						)}
						style={{ transform: "translateZ(0)" }}
					>
						{/* --- PAGES PANE --- */}
						<div className="flex grow flex-col overflow-y-auto">
							<div
								id={CANVAS_BODY_ID}
								className={cn(
									"relative",
									"print:static",
									"flex flex-auto flex-col",
									"print:block",
									"overflow-y-auto",
									"overflow-x-hidden",
									"print:overflow-visible",
									"scroll-smooth",
									"@xl/main:pt-12",
									"pt-0"
								)}
							>
								<div className="flex-1">
									<div
										className={cn(
											canvas.publishSettings?.template === "blog" &&
												"text-center"
										)}
									>
										<EditorHeader
											writable={canvasWritable}
											pagesMetadata={canvas}
											pageId={pageId}
										/>
									</div>

									<Byline activePageMetadata={pageMetadata} />

									<div className="min-h-[1rem]" />

									{/* this constrains the blog template's width but allows the canvas to still be scrollable  */}
									<div
										className={cn(
											canvas.publishSettings?.template === "blog" &&
												"blog-width"
										)}
									>
										{/* --- DOCUMENT STATE --- */}
										{match({
											showSkeleton,
										})
											.with({ showSkeleton: true }, () => (
												<LoadingSkeleton
													barCount={10}
													randomizeWidths
													showChildren={!showSkeleton}
													SkeletonWrapper={({
														children,
													}: FC["arguments"]["children"]) => (
														<div className="rich-text">
															<div className="canvas-content-row space-y-4">
																{children}
															</div>
														</div>
													)}
												/>
											))
											.with({ showSkeleton: false }, () => (
												<Editor
													ref={editorContainerRef}
													publishTemplate={
														canvas.publishSettings?.template
													}
												/>
											))
											.exhaustive()}
									</div>
									<Footer />
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>
		</>
	);
};
